<template>
  <div class="login-page">

    <!-- ── Left Panel: Slideshow ───────────────────────────────── -->
    <div class="login-left d-none d-lg-flex">

      <!-- Stacked slides (all rendered, z-index controls visibility) -->
      <div
        v-for="(slide, i) in slides"
        :key="i"
        class="slide-bg"
        :class="{ active: i === currentSlide, prev: i === prevSlide }"
        :style="{ backgroundImage: `url(${slide.image})` }"
      ></div>

      <!-- Bottom vignette -->
      <div class="slide-vignette"></div>
      <!-- Dark overlay -->
      <div class="slide-overlay"></div>

      <!-- Content on top of image -->
      <div class="slide-content">

        <!-- Logo top-left -->
        <div class="slide-logo-wrap">
          <div class="slide-logo-bg">
            <img
              :src="title.image_url"
              class="slide-logo"
              onerror="this.onerror=null; this.src='/claimify_logo.png'"
            />
          </div>
          <span class="slide-logo-name">{{ title.agency_name }}</span>
        </div>

        <!-- Spacer -->
        <div class="flex-grow-1"></div>

        <!-- Slide text -->
        <transition name="text-up" mode="out-in">
          <div class="slide-text-block" :key="currentSlide">
            <div class="slide-tag">Universal Health Coverage</div>
            <h2 class="slide-title">{{ slides[currentSlide].title }}</h2>
            <p class="slide-desc">{{ slides[currentSlide].desc }}</p>
          </div>
        </transition>

        <!-- Progress bar + dots -->
        <div class="slide-footer">
          <div class="slide-progress-wrap">
            <div class="slide-progress-track">
              <div class="slide-progress-fill" :key="currentSlide"></div>
            </div>
          </div>
          <div class="slide-dots">
            <span
              v-for="(s, i) in slides"
              :key="i"
              :class="['dot', { active: i === currentSlide }]"
              @click="goToSlide(i)"
            ></span>
          </div>
        </div>

      </div>
    </div>

    <!-- ── Right Panel: Recover Password Form ──────────────────── -->
    <div class="login-right">

      <!-- Logo pinned top-right -->
      <div class="right-logo">
        <img
          :src="title.image_url"
          onerror="this.onerror=null; this.src='/claimify_logo.png'"
        />
      </div>

      <div class="login-form-wrap">

        <!-- Back to login -->
        <router-link to="/" class="back-link mb-4">
          <i class="fe fe-arrow-left mr-1"></i> Back to sign in
        </router-link>

        <!-- Heading -->
        <div class="mb-5">
          <h3 class="font-weight-bold text-dark mb-1">Recover Password</h3>
          <p class="text-muted" style="font-size: .9rem;">Enter your email address and we'll send you a link to reset your password.</p>
        </div>

        <!-- Email -->
        <div class="form-group mb-4">
          <label class="small font-weight-bold text-secondary mb-1">Email Address</label>
          <div class="input-group input-group-lg">
            <div class="input-group-prepend">
              <span class="input-group-text field-icon">
                <i class="fe fe-mail"></i>
              </span>
            </div>
            <input
              type="email"
              required
              class="form-control field-input"
              placeholder="Enter your email"
              v-model="forgotPasswordEmail"
              @keyup.enter="sendPasswordRecoveryEmail"
            />
          </div>
          <small class="text-danger" v-if="forgotPasswordError">{{ forgotPasswordError }}</small>
        </div>

        <!-- Send button -->
        <button
          type="button"
          class="btn btn-block btn-signin font-weight-bold"
          @click="sendPasswordRecoveryEmail"
          :disabled="forgotPasswordLoading"
        >
          <span v-if="forgotPasswordLoading">
            <span class="spinner-border spinner-border-sm mr-2" role="status"></span>
            Sending…
          </span>
          <span v-else>Send Recovery Email</span>
        </button>

        <p class="text-center text-muted mt-5 mb-0" style="font-size: .75rem;">
          &copy; {{ new Date().getFullYear() }} {{ title.agency_name }}. All rights reserved.
        </p>

      </div>
    </div>

  </div>
</template>

<script>
let url = window.location.href;

export default {
  data() {
    return {
      forgotPasswordEmail: "",
      forgotPasswordLoading: false,
      forgotPasswordError: "",
      currentSlide: 0,
      prevSlide: null,
      slideTimer: null,
      title: {
        id: null,
        agency_name: "Claimify Portal",
        image_url: "/claimify_logo.png",
        favicon: "/favicon.png",
        primary_color: "#17a2b8",
        secondary_color: "#6c757d",
        tertiary_color: "#28a745",
      },
      slideImages: [
        "/background/login1.jpg",
        "/background/login2.jpg",
        "/background/login3.jpg",
      ],
      reg: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/,
    };
  },
  computed: {
    slides() {
      const texts = [
        { title: "Quality Healthcare for All",      desc: "Connecting enrollees with trusted healthcare providers across the state." },
        { title: "Empowering Communities",           desc: "Building healthier communities through comprehensive health insurance coverage." },
        { title: "Seamless Claims Processing",       desc: "Fast, transparent, and reliable health insurance claims management." },
      ];
      return this.slideImages.map((img, i) => ({
        image: img,
        ...texts[i % texts.length],
      }));
    },
  },
  mounted() {
    this.axios
      .post(`/api/v1/auth/hmo/url`, { url })
      .then((response) => {
        this.title = response.data.data;
        document.title = this.title.agency_name + " Portal";
        let favicon = document.getElementById("favicon");
        if (favicon) favicon.href = this.title.image_url;
        this.loadSlideImages(this.title.id);
      });

    this.startTimer();
  },
  beforeDestroy() {
    clearInterval(this.slideTimer);
  },
  methods: {
    startTimer() {
      this.slideTimer = setInterval(() => {
        this.prevSlide = this.currentSlide;
        this.currentSlide = (this.currentSlide + 1) % this.slides.length;
      }, 5500);
    },
    goToSlide(i) {
      this.prevSlide = this.currentSlide;
      this.currentSlide = i;
      clearInterval(this.slideTimer);
      this.startTimer();
    },

    async loadSlideImages(agencyId) {
      if (String(agencyId) !== '313923') return; // only custom backgrounds for this agency
      const folder = `/background/313923`;
      try {
        const resp = await fetch(`${folder}/manifest.json`);
        if (!resp.ok) return;
        const files = await resp.json();
        if (Array.isArray(files) && files.length) {
          this.slideImages = files.map(f => `${folder}/${f}`);
        }
      } catch {
        // keep defaults
      }
    },

    sendPasswordRecoveryEmail() {
      this.forgotPasswordError = "";
      if (!this.forgotPasswordEmail) {
        this.forgotPasswordError = "Please enter your email address";
        return;
      }

      if (!this.reg.test(this.forgotPasswordEmail)) {
        this.forgotPasswordError = "Please enter a valid email address";
        return;
      }

      this.forgotPasswordLoading = true;
      this.axios
        .post("/api/v1/forgot-password", {
          email: this.forgotPasswordEmail,
        })
        .then((response) => {
          this.forgotPasswordLoading = false;
          this.forgotPasswordEmail = "";
          this.$toasted.success(response.data.message || "Check your email for password recovery instructions", {
            position: "top-center",
            duration: 3000,
          });
        })
        .catch((error) => {
          this.forgotPasswordLoading = false;
          const message = error.response?.data?.message || "Failed to send recovery email";
          this.forgotPasswordError = message;
          this.$toasted.error(message, {
            position: "top-center",
            duration: 3000,
          });
        });
    },
  },
};
</script>

<style scoped>
/* ── Page ────────────────────────────────────────────────── */
.login-page {
  display: flex;
  height: 100vh;
  overflow: hidden;
  background: #fff;
}

/* ── Left Panel ──────────────────────────────────────────── */
.login-left {
  position: relative;
  flex: 0 0 58%;
  overflow: hidden;
  flex-direction: column;
}

/* All slides stacked; CSS controls cross-fade + Ken Burns */
.slide-bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  opacity: 0;
  transform: scale(1);
  transition: opacity 1.4s ease;
  will-change: opacity, transform;
}

.slide-bg.active {
  opacity: 1;
  animation: ken-burns 6s ease-out forwards;
}

.slide-bg.prev {
  opacity: 0;
  transition: opacity 1.4s ease;
}

@keyframes ken-burns {
  0%   { transform: scale(1);    }
  100% { transform: scale(1.09); }
}

/* Vignette from bottom */
.slide-vignette {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.72) 0%, transparent 55%);
  z-index: 1;
}

/* Blue-toned overlay */
.slide-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    155deg,
    rgba(10, 22, 50, 0.65) 0%,
    rgba(30, 58, 95, 0.45) 60%,
    rgba(37, 99, 235, 0.25) 100%
  );
  z-index: 1;
}

.slide-content {
  position: relative;
  z-index: 2;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 44px 52px 40px;
  color: #fff;
}

/* Logo top-left of left panel */
.slide-logo-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}

.slide-logo-bg {
  background: rgba(255, 255, 255, 0.92);
  border-radius: 12px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(6px);
  box-shadow: 0 2px 12px rgba(0,0,0,0.2);
  flex-shrink: 0;
}

.slide-logo {
  height: 52px;
  width: auto;
  display: block;
}

.slide-logo-name {
  font-size: .9rem;
  font-weight: 700;
  opacity: .95;
  letter-spacing: .02em;
  text-shadow: 0 1px 6px rgba(0,0,0,0.4);
}

/* Text block */
.slide-text-block {
  margin-bottom: 32px;
}

.slide-tag {
  display: inline-block;
  font-size: .72rem;
  font-weight: 700;
  letter-spacing: .12em;
  text-transform: uppercase;
  background: rgba(255,255,255,0.15);
  border: 1px solid rgba(255,255,255,0.3);
  border-radius: 20px;
  padding: 3px 12px;
  margin-bottom: 16px;
  backdrop-filter: blur(4px);
}

.slide-title {
  font-size: 2.15rem;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 14px;
  text-shadow: 0 2px 12px rgba(0,0,0,0.35);
}

.slide-desc {
  font-size: 1rem;
  opacity: .85;
  line-height: 1.7;
  max-width: 420px;
}

/* Footer: progress bar + dots */
.slide-footer {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.slide-progress-wrap {
  width: 100%;
}

.slide-progress-track {
  height: 2px;
  background: rgba(255,255,255,0.2);
  border-radius: 2px;
  overflow: hidden;
  width: 100%;
}

.slide-progress-fill {
  height: 100%;
  width: 0%;
  background: rgba(255,255,255,0.85);
  border-radius: 2px;
  animation: fill-progress 5.5s linear forwards;
}

@keyframes fill-progress {
  from { width: 0%; }
  to   { width: 100%; }
}

.slide-dots {
  display: flex;
  gap: 8px;
  align-items: center;
}

.dot {
  height: 6px;
  width: 6px;
  border-radius: 3px;
  background: rgba(255,255,255,0.35);
  cursor: pointer;
  transition: all 0.4s cubic-bezier(.4,0,.2,1);
}

.dot.active {
  width: 28px;
  background: #fff;
}

/* ── Right Panel ─────────────────────────────────────────── */
.login-right {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 40px;
  background: #fff;
  overflow-y: auto;
}

/* Small logo pinned to top-right */
.right-logo {
  position: absolute;
  top: 24px;
  right: 28px;
}

.right-logo img {
  height: 48px;
  opacity: 1;
  width: auto;
}

.login-form-wrap {
  width: 100%;
  max-width: 380px;
}

/* Back to login link */
.back-link {
  display: inline-flex;
  align-items: center;
  font-size: .85rem;
  font-weight: 600;
  color: #64748b;
  text-decoration: none;
}

.back-link:hover {
  color: #2563eb;
  text-decoration: none;
}

/* ── Form inputs ─────────────────────────────────────────── */
.field-icon {
  background: #f7f8fc;
  border-right: 0;
  border-color: #e5e7ef;
  color: #94a3b8;
}

.field-input {
  border-left: 0;
  border-color: #e5e7ef;
  background: #f7f8fc;
}

.form-control:focus {
  background: #fff;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
}

.input-group:focus-within .field-icon {
  border-color: #2563eb;
  background: #fff;
}

/* Send button */
.btn-signin {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 14px;
  font-size: 1rem;
  letter-spacing: .03em;
  box-shadow: 0 4px 20px rgba(37,99,235,0.35);
  transition: transform 0.15s, box-shadow 0.15s;
}

.btn-signin:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 24px rgba(37,99,235,0.45);
  color: #fff;
}

.btn-signin:active:not(:disabled) {
  transform: translateY(0);
}

/* ── Text transitions ────────────────────────────────────── */
.text-up-enter-active {
  transition: opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s;
}
.text-up-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.text-up-enter {
  opacity: 0;
  transform: translateY(22px);
}
.text-up-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}
</style>
